import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Phone, Clock, ArrowRight, CreditCard, Sparkles, MessageSquare, MessageCircle, Loader2, UtensilsCrossed, ShoppingBag, StickyNote, CheckCircle2, Bot, Send, X, ChevronDown, Zap, Plus, Minus, ShoppingCart, Trash2, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { WheelPicker } from "@/components/WheelPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: format date as YYYY-MM-DD in LOCAL timezone (avoids UTC offset bug)
const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BusinessProfile() {
  const { businessId } = useParams();
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [phone, setPhone] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);

  // AI Chat State
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [customerNotes, setCustomerNotes] = useState("");
  const [aiInput, setAiInput] = useState("");
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [presets, setPresets] = useState([]);

  // Tables State (for restaurants)
  const [availableTables, setAvailableTables] = useState([]);

  // Reviews State
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  // Menu State (for pizzerias)
  const [menuItems, setMenuItems] = useState([]);
  const [dailyDiscount, setDailyDiscount] = useState(0);

  // Cart State (for pizzerias)
  const [cart, setCart] = useState({});  // { item_id: { ...item, quantity } }
  const [showCart, setShowCart] = useState(false);

  // Online presence
  const [isBusinessOnline, setIsBusinessOnline] = useState(false);

  // Loyalty State
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);

  // AI Smart Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [showSatispayDialog, setShowSatispayDialog] = useState(false);
  const [satispayInfo, setSatispayInfo] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);

  const isPizzeria = business?.category === "pizzeria";
  const isRestaurant = business?.category === "restaurant" || business?.category === "hotel" || business?.category === "beach_club";

  // Free categories: no payment needed
  const FREE_CATEGORIES = ["lawyer", "accountant", "notary", "architect", "engineer"];
  // Platform fee only: 1€, no deposit
  const PLATFORM_FEE_ONLY = ["medical", "dental", "private_clinic", "psychologist", "physiotherapist", "optician", "veterinary"];
  const isFreeCategory = FREE_CATEGORIES.includes(business?.category);
  const isPlatformFeeOnly = PLATFORM_FEE_ONLY.includes(business?.category);

  useEffect(() => {
    fetchBusiness();
    fetchAvailability();
    fetchPresets();
    fetchAvailableTables();
    fetchMenu();
    fetchReviews();
    checkOnline();
    fetchLoyalty();
    const onlineInterval = setInterval(checkOnline, 30000);
    return () => clearInterval(onlineInterval);
  }, [businessId]);

  useEffect(() => {
    if (user?.phone) setPhone(user.phone);
  }, [user]);

  // For pizzerias: auto-select today and fetch slots
  useEffect(() => {
    if (isPizzeria && business) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setSelectedDate(today);
      fetchSlotsForDate(today);
    }
  }, [isPizzeria, business]);

  // For non-pizzerias: auto-select first AVAILABLE working date on load
  useEffect(() => {
    if (!isPizzeria && business && availabilityRules.length > 0) {
      const wDays = new Set(availabilityRules.filter(r => r.is_working).map(r => r.day_of_week));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let candidate = new Date(today);
      for (let i = 0; i < 30; i++) {
        const pyDay = candidate.getDay() === 0 ? 6 : candidate.getDay() - 1;
        if (candidate >= today && (wDays.size === 0 || wDays.has(pyDay))) {
          setSelectedDate(new Date(candidate));
          fetchSlotsForDate(new Date(candidate));
          return;
        }
        candidate.setDate(candidate.getDate() + 1);
      }
      setSelectedDate(today);
      fetchSlotsForDate(today);
    }
  }, [business, isPizzeria, availabilityRules]);

  const fetchBusiness = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}`);
      setBusiness(res.data);
      // Fetch trial status
      try {
        const trialRes = await axios.get(`${API}/businesses/${businessId}/trial-status`);
        setTrialInfo(trialRes.data);
      } catch {}
    } catch {
      toast.error(t("common.error"));
    }
  };

  const checkOnline = async () => {
    try {
      const res = await axios.get(`${API}/presence/${businessId}`);
      setIsBusinessOnline(res.data.is_online);
    } catch {}
  };

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability/${businessId}`);
      setAvailabilityRules(res.data);
    } catch {}
  };

  const fetchPresets = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/presets`);
      setPresets(res.data);
    } catch {}
  };

  const fetchAvailableTables = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/tables/available`);
      setAvailableTables(res.data.available_tables || []);
    } catch {}
  };

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/menu`);
      setMenuItems(res.data.items || []);
      setDailyDiscount(res.data.daily_discount || 0);
    } catch {}
  };

  const fetchLoyalty = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/loyalty/customer`, { withCredentials: true });
      if (res.data?.enabled) setLoyaltyInfo(res.data);
    } catch {}
  };


  const handleAISuggest = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai/suggest`,
        { business_id: businessId, user_input: aiInput, presets: presets.map((p) => p.content) },
        { withCredentials: true }
      );
      setAiSuggestion(res.data.suggestion);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = () => {
    setCustomerNotes(aiSuggestion);
    setAiSuggestion("");
    setAiInput("");
    setShowAIPanel(false);
  };

  const applyPreset = (preset) => {
    setCustomerNotes(preset.content);
    setShowAIPanel(false);
  };

  // AI Smart Assistant handlers
  const sendAIMessage = async () => {
    if (!aiChatInput.trim() || !user) return;
    const msg = aiChatInput.trim();
    setAiChatInput("");
    setAiChatMessages((prev) => [...prev, { role: "user", text: msg }]);
    setAiChatLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { business_id: businessId, message: msg, session_id: aiSessionId },
        { withCredentials: true }
      );
      setAiChatMessages((prev) => [...prev, { role: "ai", text: res.data.response }]);
      if (res.data.session_id) setAiSessionId(res.data.session_id);
    } catch (err) {
      setAiChatMessages((prev) => [...prev, { role: "ai", text: "Mi dispiace, c'è stato un errore. Riprova!" }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  const handleAIKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendAIMessage();
    }
  };

  // Cart functions
  const addToCart = (item) => {
    const effectivePrice = item.is_daily_special && dailyDiscount > 0
      ? item.price * (1 - dailyDiscount / 100)
      : item.price;
    setCart((prev) => {
      const existing = prev[item.item_id];
      if (existing) {
        return { ...prev, [item.item_id]: { ...existing, quantity: existing.quantity + 1 } };
      }
      return { ...prev, [item.item_id]: { ...item, price: effectivePrice, quantity: 1 } };
    });
  };

  const removeFromCart = (itemId) => {
    setCart((prev) => {
      const existing = prev[itemId];
      if (!existing) return prev;
      if (existing.quantity <= 1) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: { ...existing, quantity: existing.quantity - 1 } };
    });
  };

  const clearCart = () => setCart({});

  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const fetchSlotsForDate = async (date) => {
    const dateStr = toLocalDateStr(date);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/availability/${businessId}/${dateStr}`);
      const allSlots = res.data.slots || [];
      const booked = res.data.booked || [];
      const blocked = res.data.blocked || [];
      setSlots(allSlots);
      setBookedSlots(booked);
      setBlockedSlots(blocked);
      // Auto-select first available (non-booked, non-blocked) slot
      const firstAvail = allSlots.find(s => !booked.includes(s) && !blocked.includes(s));
      if (firstAvail) {
        setSelectedSlot(firstAvail);
      } else {
        setSelectedSlot(null);
      }
    } catch {
      setSlots([]);
      setBookedSlots([]);
      setBlockedSlots([]);
      setSelectedSlot(null);
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/${businessId}`);
      setReviews(res.data || []);
    } catch {}
  };

  const submitReview = async () => {
    if (!user) { login(); return; }
    try {
      await axios.post(`${API}/reviews`, {
        business_id: businessId,
        rating: reviewRating,
        comment: reviewComment,
      }, { withCredentials: true });
      toast.success("Recensione pubblicata!");
      setShowReviewForm(false);
      setReviewComment("");
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };


  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setSelectedSlot(null);
    fetchSlotsForDate(date);
  };

  const handleSlotClick = (slot) => {
    if (bookedSlots.includes(slot) || blockedSlots.includes(slot)) return;
    setSelectedSlot(slot);
  };

  const confirmSlotSelection = () => {
    if (!selectedSlot) return;
    if (!user) {
      login();
      return;
    }
    setShowDialog(true);
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedSlot || !phone) {
      toast.error("Inserisci il tuo numero di telefono");
      return;
    }
    if (isPizzeria && cartCount === 0) {
      toast.error("Aggiungi almeno un prodotto al carrello!");
      return;
    }
    setBooking(true);
    try {
      const dateStr = toLocalDateStr(selectedDate);

      const apiCartItems = isPizzeria ? cartItems.map((item) => ({
        item_id: item.item_id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      })) : [];

      let notes = customerNotes;
      if (isPizzeria && cartItems.length > 0) {
        const cartSummary = cartItems.map((i) => `${i.quantity}x ${i.name}`).join(", ");
        notes = notes ? `${cartSummary} | ${notes}` : cartSummary;
      }

      const bookingRes = await axios.post(
        `${API}/bookings`,
        {
          business_id: businessId,
          date: dateStr,
          time_slot: selectedSlot,
          customer_phone: phone,
          customer_notes: notes,
          cart_items: apiCartItems,
        },
        { withCredentials: true }
      );

      // Free category: booking confirmed directly, no payment
      if (bookingRes.data.is_free) {
        toast.success("Prenotazione confermata! Nessun pagamento richiesto.");
        setShowDialog(false);
        navigate("/dashboard");
        return;
      }

      if (paymentMethod === "satispay") {
        const satRes = await axios.post(
          `${API}/satispay/create-payment`,
          { booking_id: bookingRes.data.booking_id, origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (satRes.data.status === "pending_satispay") {
          setSatispayInfo({
            phone: satRes.data.phone,
            amount: satRes.data.amount,
            platformFee: satRes.data.platform_fee || 1,
            businessDeposit: satRes.data.business_deposit || 0,
            businessName: satRes.data.business_name,
            platformRecipient: satRes.data.platform_recipient || "BookEasy",
            platformPhone: satRes.data.platform_phone || satRes.data.phone,
            businessSatispayPhone: satRes.data.business_satispay_phone || "",
            bookingId: bookingRes.data.booking_id,
            paymentId: satRes.data.payment_id,
          });
          setShowDialog(false);
          setShowSatispayDialog(true);
        } else if (satRes.data.redirect_url) {
          window.location.href = satRes.data.redirect_url;
        }
      } else {
        const payRes = await axios.post(
          `${API}/payments/checkout`,
          { booking_id: bookingRes.data.booking_id, origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (payRes.data.free) {
          toast.success("Prenotazione confermata!");
          setShowDialog(false);
          navigate("/dashboard");
          return;
        }
        if (payRes.data.url) window.location.href = payRes.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setBooking(false);
    }
  };

  const workingDays = new Set(
    availabilityRules.filter((r) => r.is_working).map((r) => r.day_of_week)
  );

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (workingDays.size > 0 && !workingDays.has(date.getDay() === 0 ? 6 : date.getDay() - 1)) return true;
    return false;
  };

  // Generate date items for wheel picker (next 30 days)
  const dateWheelItems = (() => {
    const days = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
    const months = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
    const items = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      d.setHours(0, 0, 0, 0);
      const disabled = isDateDisabled(d);
      const label = i === 0 ? "Oggi" : i === 1 ? "Domani" : `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
      items.push({
        value: toLocalDateStr(d),
        label,
        disabled,
        date: d,
      });
    }
    return items;
  })();

  // Generate time slot items for wheel picker
  const timeWheelItems = slots.map((slot) => {
    const isBooked = bookedSlots.includes(slot);
    const isBlocked = blockedSlots.includes(slot);
    return {
      value: slot,
      label: slot,
      disabled: isBooked || isBlocked,
      booked: isBooked,
      blocked: isBlocked,
    };
  });

  const deposit = isFreeCategory ? 0 : (isPlatformFeeOnly ? 0 : (business?.deposit_amount || 5.0));
  const platformFee = isFreeCategory ? 0 : (trialInfo?.is_trial ? 0 : 1.0);
  const total = isFreeCategory ? 0 : (isPizzeria && cartTotal > 0 ? cartTotal + platformFee : deposit + platformFee);

  // Group menu items by category
  const menuByCategory = menuItems.reduce((acc, item) => {
    const cat = item.category || "altro";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (!business) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#E2725B] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="pt-28 pb-16 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Business Header */}
          <div className="mb-10" data-testid="business-header">
            <div className="flex items-center gap-3 mb-3">
              <Badge className="rounded-full border border-[rgba(226,114,91,0.2)] bg-[#E2725B]/5 text-[#E2725B] text-xs px-3 py-1 font-bold">
                {t(`cat.${business.category}`)}
              </Badge>
              {isPizzeria && (
                <Badge className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs px-3 py-1 font-bold" data-testid="takeaway-badge">
                  <ShoppingBag className="inline h-3 w-3 mr-1" />
                  {t("pizzeria.today_order")}
                </Badge>
              )}
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#2A1B14] mb-4"
              data-testid="business-profile-name"
            >
              {business.name}
              {/* Online Status */}
              <span className="inline-flex items-center gap-2 ml-4 align-middle" data-testid="business-online-status">
                <span className="relative flex h-3 w-3">
                  {isBusinessOnline ? (
                    <>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                    </>
                  ) : (
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-400" />
                  )}
                </span>
                <span className={`text-sm font-medium ${isBusinessOnline ? "text-emerald-600" : "text-red-400"}`}>
                  {isBusinessOnline ? "Online" : "Offline"}
                </span>
              </span>
            </h1>
            {business.description && (
              <p className="text-base text-[#6A564A] leading-relaxed max-w-2xl mb-4">
                {business.description}
              </p>
            )}
            <div className="flex flex-wrap gap-6 text-sm text-[#6A564A]">
              {business.address && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-[#E2725B]" strokeWidth={1.5} />
                  {business.address}
                </span>
              )}
              {business.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-[#E2725B]" strokeWidth={1.5} />
                  {business.phone}
                </span>
              )}
              {business.whatsapp && (
                <a
                  href={`https://wa.me/${business.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-full font-bold text-sm shadow-lg hover:bg-[#128C7E] hover:scale-105 active:scale-95 transition-all"
                  data-testid="whatsapp-link"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
              )}
              {!isFreeCategory && (
                <span className="flex items-center gap-1.5">
                  <CreditCard className="h-4 w-4 text-[#E2725B]" strokeWidth={1.5} />
                  {isPlatformFeeOnly ? "Commissione: 1.00 EUR" : `${t("book.deposit")}: ${deposit.toFixed(2)} EUR`}
                </span>
              )}
              {isFreeCategory && (
                <span className="flex items-center gap-1.5 text-emerald-600 font-semibold">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                  Prenotazione Gratuita
                </span>
              )}
            </div>
          </div>

          {/* Loyalty Card (for customers) */}
          {loyaltyInfo && (
            <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-5 shadow-sm" data-testid="loyalty-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Star className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-amber-900">Programma Fedelta</h3>
                  <p className="text-xs text-amber-700">{loyaltyInfo.reward_description || "Accumula punti e ottieni un premio!"}</p>
                </div>
              </div>
              <div className="relative h-4 bg-amber-100 rounded-full overflow-hidden mb-2">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                  style={{ width: `${loyaltyInfo.progress_percent}%` }}
                />
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-amber-800">{loyaltyInfo.points} / {loyaltyInfo.threshold} visite</span>
                {loyaltyInfo.has_reward ? (
                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full font-bold animate-pulse">HAI UN PREMIO!</span>
                ) : (
                  <span className="text-amber-600">Mancano {loyaltyInfo.threshold - loyaltyInfo.points} visite</span>
                )}
              </div>
            </div>
          )}

          {/* Menu Section (for pizzerias - with cart) */}
          {isPizzeria && menuItems.length > 0 && (
            <div className="mb-10 bg-white rounded-3xl border border-[rgba(226,114,91,0.1)] shadow-warm overflow-hidden" data-testid="pizzeria-menu-section">
              <div className="bg-gradient-to-r from-[#E2725B] to-[#F39C12] px-8 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UtensilsCrossed className="h-6 w-6 text-white" strokeWidth={1.5} />
                    <h2 className="text-xl font-bold text-white">{t("pizzeria.menu_title")}</h2>
                  </div>
                  {cartCount > 0 && (
                    <button
                      onClick={() => setShowCart(!showCart)}
                      className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full transition-all"
                      data-testid="cart-toggle-btn"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span className="font-bold">{cartCount}</span>
                      <span className="text-sm">{cartTotal.toFixed(2)}€</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Cart Summary (collapsible) */}
              <AnimatePresence>
                {showCart && cartCount > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-amber-50 border-b border-amber-200 px-6 py-4" data-testid="cart-summary">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Il tuo ordine
                        </h3>
                        <button
                          onClick={clearCart}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          data-testid="clear-cart-btn"
                        >
                          <Trash2 className="h-3 w-3" />
                          Svuota
                        </button>
                      </div>
                      <div className="space-y-2">
                        {cartItems.map((item) => (
                          <div key={item.item_id} className="flex items-center justify-between text-sm" data-testid={`cart-item-${item.item_id}`}>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1 bg-white rounded-lg border border-amber-200">
                                <button onClick={() => removeFromCart(item.item_id)} className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-l-lg transition-colors">
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-6 text-center font-bold text-amber-800">{item.quantity}</span>
                                <button onClick={() => addToCart(item)} className="w-7 h-7 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 rounded-r-lg transition-colors">
                                  <Plus className="h-3 w-3" />
                                </button>
                              </div>
                              <span className="text-slate-700">{item.name}</span>
                            </div>
                            <span className="font-bold text-amber-800">{(item.price * item.quantity).toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-amber-300 mt-3 pt-3 flex justify-between items-center">
                        <span className="text-sm text-slate-600">Subtotale</span>
                        <span className="font-black text-lg text-[#E2725B]">{cartTotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-slate-400 mt-1">
                        <span>+ 1.00€ commissione piattaforma</span>
                        <span className="font-bold text-slate-600">Totale: {(cartTotal + 1).toFixed(2)}€</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="p-6">
                {Object.entries(menuByCategory).map(([cat, items]) => (
                  <div key={cat} className="mb-6 last:mb-0">
                    <h3 className="text-sm uppercase tracking-widest text-orange-600 font-bold mb-3 border-b border-orange-100 pb-2">
                      {cat}
                    </h3>
                    <div className="space-y-2">
                      {items.map((item) => {
                        const effectivePrice = item.is_daily_special && dailyDiscount > 0
                          ? item.price * (1 - dailyDiscount / 100)
                          : item.price;
                        const inCart = cart[item.item_id];
                        return (
                          <div
                            key={item.item_id}
                            className={`flex justify-between items-center p-3 rounded-xl transition-all ${
                              inCart
                                ? "bg-emerald-50 border-2 border-emerald-300 shadow-sm"
                                : item.is_daily_special
                                  ? "bg-amber-50 border border-amber-200"
                                  : "hover:bg-orange-50 border border-transparent"
                            }`}
                            data-testid={`menu-display-${item.item_id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-800">{item.name}</span>
                                {item.is_daily_special && (
                                  <Badge className="rounded-full text-[10px] px-2 bg-amber-100 text-amber-700 border-amber-300">
                                    -{dailyDiscount}%
                                  </Badge>
                                )}
                                {inCart && (
                                  <Badge className="rounded-full text-[10px] px-2 bg-emerald-100 text-emerald-700 border-emerald-300">
                                    x{inCart.quantity}
                                  </Badge>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                              <span className="font-bold text-orange-600 whitespace-nowrap">
                                {item.is_daily_special && dailyDiscount > 0 ? (
                                  <>
                                    <span className="line-through text-slate-300 mr-1 text-sm">{item.price.toFixed(2)}€</span>
                                    {effectivePrice.toFixed(2)}€
                                  </>
                                ) : (
                                  `${item.price.toFixed(2)}€`
                                )}
                              </span>
                              {/* Cart controls */}
                              <div className="flex items-center gap-1">
                                {inCart && (
                                  <button
                                    onClick={() => removeFromCart(item.item_id)}
                                    className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center text-red-600 transition-colors"
                                    data-testid={`remove-from-cart-${item.item_id}`}
                                  >
                                    <Minus className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => addToCart(item)}
                                  className="w-8 h-8 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center text-emerald-700 transition-colors"
                                  data-testid={`add-to-cart-${item.item_id}`}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Tables (for restaurants) */}
          {isRestaurant && availableTables.length > 0 && (
            <div className="mb-10 bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden" data-testid="available-tables-section">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                <div className="flex items-center gap-3">
                  <UtensilsCrossed className="h-6 w-6 text-white" />
                  <h2 className="text-xl font-bold text-white">
                    Tavoli Disponibili Ora ({availableTables.length})
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-3">
                  {availableTables.map((table) => (
                    <div
                      key={table.table_number}
                      className="aspect-square flex flex-col items-center justify-center bg-emerald-50 border-2 border-emerald-300 rounded-xl shadow-sm"
                      data-testid={`available-table-${table.table_number}`}
                    >
                      <span className="font-bold text-lg text-emerald-700">{table.table_number}</span>
                      {table.freed_at && (
                        <span className="text-[9px] text-emerald-500">
                          {new Date(table.freed_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Booking Section — Wheel Pickers */}
          <div className={`grid grid-cols-1 ${isPizzeria ? "" : "lg:grid-cols-2"} gap-8`}>
            {/* Date Wheel (NOT shown for pizzerias — today is forced) */}
            {!isPizzeria && (
              <div>
                <h2 className="text-xs uppercase tracking-widest text-[#09090B]/50 font-bold mb-4">
                  {t("book.select_date")}
                </h2>
                <div className="bg-white rounded-2xl border-2 border-[#09090B] shadow-brutal p-6" data-testid="date-wheel-section">
                  <WheelPicker
                    items={dateWheelItems}
                    selected={selectedDate ? toLocalDateStr(selectedDate) : null}
                    onSelect={(val) => {
                      const d = new Date(val + "T00:00:00");
                      handleDateSelect(d);
                    }}
                    testIdPrefix="date-wheel"
                    renderItem={(item, isSelected) => (
                      <div className={`flex items-center gap-3 px-4 w-full justify-center ${item.disabled ? "line-through opacity-30" : ""}`}>
                        <span className={`${isSelected ? "font-black text-lg text-[#09090B]" : "text-[#3F3F46]"}`}>
                          {item.label}
                        </span>
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Time Wheel */}
            <div className={isPizzeria ? "w-full" : ""}>
              <h2 className="text-xs uppercase tracking-widest text-[#09090B]/50 font-bold mb-4">
                {isPizzeria ? t("pizzeria.select_pickup") : t("book.select_time")}
              </h2>
              <div className="bg-white rounded-2xl border-2 border-[#09090B] shadow-brutal p-6 min-h-[300px]" data-testid="time-wheel-section">
                {!selectedDate ? (
                  <p className="text-sm text-[#3F3F46] text-center py-16">{t("book.select_date")}</p>
                ) : loadingSlots ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-[#FF4A00]" />
                  </div>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-[#3F3F46] text-center py-16">{t("book.closed")}</p>
                ) : (
                  <div>
                    {/* Legend */}
                    <div className="flex items-center gap-4 mb-5 text-xs flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 bg-[#06D6A0] border-2 border-[#09090B] rounded-full" />
                        {t("book.available")}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 bg-red-500 border-2 border-red-700 rounded-full" />
                        {t("book.booked")}
                      </span>
                    </div>
                    <WheelPicker
                      items={timeWheelItems}
                      selected={selectedSlot}
                      onSelect={(val) => handleSlotClick(val)}
                      testIdPrefix="time-wheel"
                      renderItem={(item, isSelected) => (
                        <div className={`flex items-center justify-center gap-3 w-full px-4
                          ${item.booked ? "bg-red-50" : item.blocked ? "" : ""}
                        `}>
                          <Clock className={`h-4 w-4 ${item.booked ? "text-red-500" : isSelected ? "text-[#09090B]" : "text-[#71717A]"}`} />
                          <span className={`
                            ${item.booked ? "line-through text-red-500 font-bold" : ""}
                            ${item.blocked ? "line-through opacity-30" : ""}
                            ${isSelected && !item.disabled ? "font-black text-lg text-[#09090B]" : ""}
                          `}>
                            {item.label}
                          </span>
                          {item.booked && (
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-red-500 text-white px-2 py-0.5 rounded-md border border-red-700">
                              Occupato
                            </span>
                          )}
                          {isSelected && !item.disabled && (
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#06D6A0] text-[#09090B] px-2 py-0.5 rounded-md border border-[#09090B]">
                              Scelto
                            </span>
                          )}
                        </div>
                      )}
                    />
                    {/* Confirm button */}
                    {selectedSlot && !bookedSlots.includes(selectedSlot) && (
                      <button
                        onClick={confirmSlotSelection}
                        className="w-full mt-4 btn-brutal bg-[#FF4A00] text-white text-sm py-3 flex items-center justify-center gap-2"
                        data-testid="confirm-slot-btn"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Conferma {selectedSlot}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Notes / AI Request Panel */}
          {selectedSlot && user && (
            <div className="mt-8">
              <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {isPizzeria ? (
                      <StickyNote className="h-5 w-5 text-orange-500" />
                    ) : (
                      <MessageSquare className="h-5 w-5 text-orange-500" />
                    )}
                    <h3 className="text-sm font-bold text-slate-800">
                      {isPizzeria ? t("pizzeria.order_notes") : t("ai.panel.title")}
                    </h3>
                  </div>
                  {!isPizzeria && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAIPanel(!showAIPanel)}
                      className="rounded-full border-orange-200 text-xs hover:bg-orange-50"
                      data-testid="toggle-ai-panel"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1 text-orange-500" />
                      {showAIPanel ? "Chiudi" : t("ai.improve")}
                    </Button>
                  )}
                </div>

                <Textarea
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder={isPizzeria ? t("pizzeria.order_notes.placeholder") : t("ai.placeholder")}
                  className="rounded-xl border-orange-200 min-h-[100px] mb-4 focus:ring-orange-300 focus:border-orange-400"
                  data-testid="customer-notes-input"
                />

                {/* Quick Presets */}
                {presets.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">{t("ai.presets")}</p>
                    <div className="flex flex-wrap gap-2">
                      {presets.slice(0, 4).map((preset) => (
                        <button
                          key={preset.preset_id}
                          onClick={() => applyPreset(preset)}
                          className="text-xs px-3 py-1.5 rounded-full border border-orange-200 hover:bg-orange-50 hover:border-orange-400 transition-colors"
                          data-testid={`preset-${preset.preset_id}`}
                        >
                          {preset.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Enhancement Panel */}
                {showAIPanel && !isPizzeria && (
                  <div className="border-t border-orange-100 pt-4 mt-4">
                    <p className="text-xs text-slate-400 mb-3">{t("ai.panel.desc")}</p>
                    <div className="flex gap-2">
                      <Input
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        placeholder="es. voglio un taglio corto con sfumatura..."
                        className="rounded-xl border-orange-200 flex-1"
                        data-testid="ai-input"
                      />
                      <Button
                        onClick={handleAISuggest}
                        disabled={aiLoading || !aiInput.trim()}
                        className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0"
                        data-testid="ai-suggest-btn"
                      >
                        {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      </Button>
                    </div>
                    {aiSuggestion && (
                      <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                        <p className="text-xs uppercase tracking-widest text-orange-600 mb-2">Suggerimento IA</p>
                        <p className="text-sm text-slate-700 mb-3">{aiSuggestion}</p>
                        <Button
                          onClick={applyAISuggestion}
                          size="sm"
                          className="rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0"
                          data-testid="apply-ai-suggestion"
                        >
                          {t("ai.use")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Confirmation Dialog — Warm & Inviting */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setSelectedSlot(null); }}>
        <DialogContent className="rounded-3xl border-[rgba(226,114,91,0.15)] bg-white max-w-md shadow-warm-lg" data-testid="booking-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200">
                <CheckCircle2 className="h-6 w-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <DialogTitle className="font-heading text-xl font-bold text-[#2A1B14]">
                  {t("book.confirm_friendly")}
                </DialogTitle>
                <p className="text-xs text-[#6A564A] mt-0.5">{t("book.almost_done")}</p>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-br from-[#E2725B]/5 to-[#F39C12]/5 rounded-2xl p-5 space-y-2 text-sm border border-[rgba(226,114,91,0.1)]">
              <div className="flex justify-between">
                <span className="text-slate-500">{business.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{selectedDate?.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</span>
                <span className="font-bold text-orange-600 text-lg">{selectedSlot}</span>
              </div>

              {/* Cart Items Summary (pizzeria) */}
              {isPizzeria && cartItems.length > 0 && (
                <div className="border-t border-orange-200 pt-2 mt-2 space-y-1">
                  <p className="text-xs uppercase tracking-widest text-orange-500 font-bold mb-1">Ordine</p>
                  {cartItems.map((item) => (
                    <div key={item.item_id} className="flex justify-between text-xs">
                      <span className="text-slate-500">{item.quantity}x {item.name}</span>
                      <span className="text-slate-700">{(item.price * item.quantity).toFixed(2)}€</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1 border-t border-orange-100">
                    <span className="text-slate-500">Subtotale ordine</span>
                    <span className="font-semibold text-slate-800">{cartTotal.toFixed(2)}€</span>
                  </div>
                </div>
              )}

              {/* Pricing section */}
              {isFreeCategory ? (
                <div className="border-t border-emerald-200 pt-3 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-700 font-bold">Prenotazione Gratuita</span>
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-bold">GRATIS</span>
                  </div>
                </div>
              ) : (
                <div className={`${isPizzeria && cartItems.length > 0 ? "" : "border-t border-orange-200 pt-2 mt-2"} space-y-1`}>
                  {!isPizzeria && deposit > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t("book.deposit")}</span>
                      <span className="text-slate-700">{deposit.toFixed(2)} EUR</span>
                    </div>
                  )}
                  {trialInfo?.is_trial ? (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Commissione piattaforma</span>
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold">GRATIS (prova)</span>
                    </div>
                  ) : platformFee > 0 ? (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t("book.platform_fee")}</span>
                      <span className="text-slate-700">{platformFee.toFixed(2)} EUR</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between font-bold pt-2 border-t border-[rgba(226,114,91,0.15)] text-base">
                    <span className="text-[#2A1B14]">{t("book.total")}</span>
                    <span className="gradient-text">{total.toFixed(2)} EUR</span>
                  </div>
                  {/* Info commissione per il cliente */}
                  {!trialInfo?.is_trial && platformFee > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1 leading-relaxed" data-testid="fee-info-text">
                      La commissione di {platformFee.toFixed(2)}€ verra scalata dal tuo pagamento definitivo al completamento del servizio.
                    </p>
                  )}
                  {trialInfo?.is_trial && (
                    <p className="text-[10px] text-emerald-500 mt-1 leading-relaxed" data-testid="trial-info-text">
                      Questa attivita e in periodo di prova: nessuna commissione aggiuntiva per te!
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Payment Method Selector — HIDDEN for free categories */}
            {!isFreeCategory && (
              <div data-testid="payment-method-selector">
                <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                  {t("payment.method")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPaymentMethod("stripe")}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === "stripe"
                        ? "border-[#E2725B] bg-[#E2725B]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid="payment-stripe-btn"
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-[#E2725B]" />
                      <span className="text-sm font-semibold">Stripe</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Carta {total.toFixed(2)}€</p>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("satispay")}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === "satispay"
                        ? "border-[#E2725B] bg-[#E2725B]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid="payment-satispay-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-semibold">Satispay</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Paga {total.toFixed(2)}€ dall'app</p>
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                {t("book.phone")} *
              </label>
              <Input
                data-testid="booking-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("book.phone.placeholder")}
                className="rounded-xl border-orange-200 h-12 focus:ring-orange-300"
                required
              />
            </div>

            {customerNotes && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs uppercase tracking-widest text-amber-600 mb-1">
                  {isPizzeria ? t("pizzeria.order_notes") : t("book.notes")}
                </p>
                <p className="text-sm text-slate-700">{customerNotes}</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col gap-2">
            {!isFreeCategory && <p className="text-[10px] text-slate-400 text-center w-full">{t("cancel.policy")}</p>}
            <div className="flex gap-2 w-full justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  setSelectedSlot(null);
                }}
                className="rounded-xl border-slate-200"
                data-testid="cancel-booking-button"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleBooking}
                disabled={booking || !phone}
                className={`rounded-full text-white hover:shadow-xl border-0 transition-all duration-200 disabled:opacity-50 px-8 py-6 font-bold hover:scale-105 active:scale-95 ${
                  isFreeCategory
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:shadow-emerald-200"
                    : "bg-gradient-to-r from-[#E2725B] to-[#F39C12] hover:shadow-[#E2725B]/25"
                }`}
                data-testid="confirm-pay-button"
              >
                {booking ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {booking ? t("common.loading") : isFreeCategory ? "Conferma Prenotazione" : t("book.secure_spot")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Smart Assistant Floating Widget */}
      <AnimatePresence>
        {showAIAssistant && user && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-3xl shadow-2xl border border-[rgba(226,114,91,0.15)] overflow-hidden z-50"
            data-testid="ai-assistant-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#E2725B] to-[#F39C12] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">{t("ai.assistant")}</h3>
                  <p className="text-[10px] text-white/70">{business?.name}</p>
                </div>
              </div>
              <button onClick={() => setShowAIAssistant(false)} className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="h-[300px] overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
              {aiChatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="h-10 w-10 mx-auto text-[#E2725B]/30 mb-3" />
                  <p className="text-sm text-slate-400">{t("ai.assistant.welcome")}</p>
                </div>
              )}
              {aiChatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                      msg.role === "user"
                        ? "bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white"
                        : "bg-gray-100 text-slate-700"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {aiChatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-[#E2725B]" />
                    <span className="text-sm text-slate-400">{t("ai.assistant.thinking")}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 p-3 flex gap-2">
              <input
                value={aiChatInput}
                onChange={(e) => setAiChatInput(e.target.value)}
                onKeyDown={handleAIKeyPress}
                placeholder={t("ai.assistant.placeholder")}
                className="flex-1 text-sm px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-[#E2725B] focus:ring-1 focus:ring-[#E2725B]/20"
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendAIMessage}
                disabled={aiChatLoading || !aiChatInput.trim()}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#E2725B] to-[#F39C12] flex items-center justify-center text-white disabled:opacity-50 hover:shadow-lg transition-all"
                data-testid="ai-chat-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating AI Assistant Button */}
      {user && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowAIAssistant(!showAIAssistant)}
          className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-xl flex items-center justify-center z-50 transition-colors ${
            showAIAssistant
              ? "bg-slate-700 shadow-slate-300"
              : "bg-gradient-to-r from-[#E2725B] to-[#F39C12] shadow-[#E2725B]/30"
          }`}
          data-testid="ai-assistant-toggle"
        >
          {showAIAssistant ? (
            <ChevronDown className="h-6 w-6 text-white" />
          ) : (
            <Bot className="h-6 w-6 text-white" />
          )}
        </motion.button>
      )}

      {/* Floating Cart Bar (for pizzerias with items in cart) */}
      <AnimatePresence>
        {isPizzeria && cartCount > 0 && !showDialog && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t-2 border-[#E2725B] px-6 py-4 z-40 shadow-2xl"
            data-testid="floating-cart-bar"
          >
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#E2725B] flex items-center justify-center text-white font-bold">
                  {cartCount}
                </div>
                <div>
                  <p className="text-sm font-bold text-[#2A1B14]">{cartItems.map((i) => `${i.quantity}x ${i.name}`).join(", ").substring(0, 50)}{cartItems.map((i) => `${i.quantity}x ${i.name}`).join(", ").length > 50 ? "..." : ""}</p>
                  <p className="text-xs text-slate-500">Totale: <span className="font-bold text-[#E2725B]">{(cartTotal + 1).toFixed(2)}€</span> (incl. 1€ piattaforma)</p>
                </div>
              </div>
              {selectedSlot ? (
                <Button
                  onClick={() => setShowDialog(true)}
                  className="rounded-full bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white font-bold px-6 py-5 hover:shadow-xl hover:scale-105 active:scale-95 transition-all border-0"
                  data-testid="cart-checkout-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Ordina Ora
                </Button>
              ) : (
                <p className="text-xs text-slate-400 text-right max-w-[140px]">Scegli un orario di ritiro per procedere</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews Section */}
      <div className="mt-10 max-w-4xl mx-auto px-6" data-testid="reviews-section">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-xl font-bold text-[#09090B]">Recensioni</h2>
            {business?.avg_rating > 0 && (
              <div className="flex items-center gap-1 bg-[#FFE600] border-2 border-[#09090B] rounded-lg px-2.5 py-1 shadow-brutal-sm">
                <Star className="h-4 w-4 text-[#09090B] fill-[#09090B]" />
                <span className="font-bold text-sm text-[#09090B]">{business.avg_rating}</span>
                <span className="text-xs text-[#09090B]/60">({business.review_count || reviews.length})</span>
              </div>
            )}
          </div>
          {user && !showReviewForm && (
            <button
              onClick={() => setShowReviewForm(true)}
              className="btn-brutal bg-[#00E5FF] text-[#09090B] text-xs"
              data-testid="write-review-btn"
            >
              Scrivi Recensione
            </button>
          )}
        </div>

        {/* Review Form */}
        {showReviewForm && (
          <div className="bg-white border-2 border-[#09090B] rounded-2xl p-5 mb-6 shadow-brutal" data-testid="review-form">
            <div className="flex items-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setReviewRating(star)}
                  className="p-1 transition-transform hover:scale-125"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`h-7 w-7 ${star <= reviewRating ? "fill-[#FFE600] text-[#09090B]" : "text-[#D4D4D8]"}`}
                    strokeWidth={2}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm font-bold text-[#09090B]">{reviewRating}/5</span>
            </div>
            <Textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Racconta la tua esperienza..."
              className="rounded-xl border-2 border-[#09090B] mb-4 min-h-[80px]"
              data-testid="review-comment-input"
            />
            <div className="flex gap-3">
              <button onClick={submitReview} className="btn-brutal bg-[#FFE600] text-[#09090B] text-sm" data-testid="submit-review-btn">
                Pubblica
              </button>
              <button onClick={() => setShowReviewForm(false)} className="btn-brutal bg-white text-[#09090B] text-sm">
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#09090B]/20 rounded-2xl p-8 text-center">
            <p className="text-[#71717A]">Nessuna recensione ancora. Sii il primo!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map((rev) => (
              <div key={rev.review_id} className="bg-white border-2 border-[#09090B] rounded-2xl p-5 shadow-brutal-sm" data-testid={`review-${rev.review_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#00E5FF] border-2 border-[#09090B] flex items-center justify-center font-bold text-sm text-[#09090B]">
                      {rev.user_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <span className="font-bold text-[#09090B] text-sm">{rev.user_name}</span>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= rev.rating ? "fill-[#FFE600] text-[#09090B]" : "text-[#D4D4D8]"}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-[#71717A]">
                    {new Date(rev.created_at).toLocaleDateString("it-IT")}
                  </span>
                </div>
                {rev.comment && <p className="text-sm text-[#3F3F46] mt-2">{rev.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Satispay Payment Dialog */}
      <Dialog open={showSatispayDialog} onOpenChange={setShowSatispayDialog}>
        <DialogContent className="rounded-3xl border-0 shadow-2xl max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-black text-[#2A1B14]">
              Paga con Satispay
            </DialogTitle>
          </DialogHeader>
          {satispayInfo && (
            <div className="space-y-5 py-2">
              {/* Satispay Red branded header */}
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white text-center">
                <p className="text-sm opacity-80 mb-1">Totale da pagare</p>
                <p className="text-4xl font-black">{satispayInfo.amount.toFixed(2)}€</p>
                <p className="text-sm opacity-80 mt-2">per {satispayInfo.businessName}</p>
              </div>

              {/* Payment breakdown */}
              <div className="space-y-3">
                {/* Platform fee - always to Cristian Senese */}
                <div className="border-2 border-red-200 rounded-xl p-4 bg-red-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs uppercase tracking-widest text-red-500 font-bold">Commissione Piattaforma</span>
                    <span className="text-lg font-black text-red-600">{satispayInfo.platformFee.toFixed(2)}€</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">CS</div>
                    <div>
                      <p className="font-bold text-sm text-slate-800">{satispayInfo.platformRecipient}</p>
                      <p className="text-lg font-black text-red-600 tracking-wide">{satispayInfo.platformPhone}</p>
                    </div>
                  </div>
                </div>

                {/* Business deposit - if > 0 */}
                {satispayInfo.businessDeposit > 0 && satispayInfo.businessSatispayPhone && (
                  <div className="border-2 border-amber-200 rounded-xl p-4 bg-amber-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs uppercase tracking-widest text-amber-600 font-bold">Cauzione Attivita</span>
                      <span className="text-lg font-black text-amber-700">{satispayInfo.businessDeposit.toFixed(2)}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">
                        {satispayInfo.businessName?.[0] || "A"}
                      </div>
                      <div>
                        <p className="font-bold text-sm text-slate-800">{satispayInfo.businessName}</p>
                        <p className="text-lg font-black text-amber-700 tracking-wide">{satispayInfo.businessSatispayPhone}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">1</div>
                  <p className="text-sm text-slate-700">Apri l'app <strong>Satispay</strong> sul tuo telefono</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">2</div>
                  <p className="text-sm text-slate-700">Invia <strong>{satispayInfo.platformFee.toFixed(2)}€</strong> a <strong>{satispayInfo.platformRecipient}</strong> ({satispayInfo.platformPhone})</p>
                </div>
                {satispayInfo.businessDeposit > 0 && satispayInfo.businessSatispayPhone && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold shrink-0">3</div>
                    <p className="text-sm text-slate-700">Invia <strong>{satispayInfo.businessDeposit.toFixed(2)}€</strong> a <strong>{satispayInfo.businessName}</strong> ({satispayInfo.businessSatispayPhone})</p>
                  </div>
                )}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold shrink-0">{satispayInfo.businessDeposit > 0 && satispayInfo.businessSatispayPhone ? "4" : "3"}</div>
                  <p className="text-sm text-slate-700">La prenotazione sara confermata automaticamente</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    const deepLink = `satispay://send?phone=${encodeURIComponent(satispayInfo.platformPhone)}&amount=${Math.round(satispayInfo.platformFee * 100)}&currency=EUR`;
                    window.open(deepLink, "_blank");
                  }}
                  className="flex-1 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold h-12 hover:shadow-lg border-0"
                  data-testid="satispay-open-app-btn"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Apri Satispay
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSatispayDialog(false);
                    toast.success("Prenotazione creata! In attesa di conferma pagamento.");
                    navigate("/dashboard");
                  }}
                  className="flex-1 rounded-xl h-12 border-slate-200"
                  data-testid="satispay-done-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Ho Pagato
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 text-center">
                La tua prenotazione sara confermata appena verificato il pagamento
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
